import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { JobworkProps, JobworkPartyItemsProps } from "../../types/jobwork";
import { ItemsProps } from "../../types/item";
import { ProcessesProps } from "../../types/process";
import { PartiesProps } from "../../types/parties";

export interface JobworkState {
  jobwork: JobworkProps[];
  totalJobwork: number;
  totalAmount: number;
  isJobworkModalOpen: boolean;
  isJobworkPhotoModalOpen: string | null;
  editJobwork: JobworkProps | null;
  editPartyItems: JobworkPartyItemsProps[];
  items: ItemsProps[];
  processes: ProcessesProps[];
  parties: PartiesProps[];
  loading: boolean;
  error: string | null;
}

const initialState: JobworkState = {
  jobwork: [],
  totalJobwork: 0,
  totalAmount: 0,
  isJobworkModalOpen: false,
  isJobworkPhotoModalOpen: null,
  editJobwork: null,
  editPartyItems: [],
  items: [],
  processes: [],
  parties: [],
  loading: false,
  error: null,
};

type FetchJobworkListDataProps = {
  page: number;
  size?: number;
  fromDate?: string | null;
  toDate?: string | null;
  search?: string | null;
  jobwork_party_id?: number | null;
  item_id?: number | null;
  process_id?: number | null;
  status?: number | null;
};

export const fetchJobworkListData = createAsyncThunk(
  "fetchJobworkListData",
  async (req: FetchJobworkListDataProps) => {
    let response: any;
    await GET(`/v1/admin/jobwork`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemsList = createAsyncThunk(
  "fetchItemsListForJobworkFilter",
  async () => {
    let response: any;
    await GET(`/v1/admin/items`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchProcessesList = createAsyncThunk(
  "fetchProcessesListForJobworkFilter",
  async () => {
    let response: any;
    await GET(`/v1/admin/processes`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPartiesList = createAsyncThunk(
  "fetchPartiesListForJobworkFilter",
  async () => {
    let response: any;
    await GET(`/v1/admin/jobwork-parties`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchEditPartyItemsList = createAsyncThunk(
  "fetchEditPartyItemsList",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/jobwork/parties/${id}/items`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const jobworkSlice = createSlice({
  name: "jobwork",
  initialState,
  reducers: {
    toggleJobworkModal: (state, action: PayloadAction<boolean>) => {
      state.isJobworkModalOpen = action.payload;
    },
    toggleJobworkPhotoModal: (state, action: PayloadAction<string | null>) => {
      state.isJobworkPhotoModalOpen = action.payload;
    },
    setEditJobwork: (state, action: PayloadAction<JobworkProps | null>) => {
      state.editJobwork = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobworkListData.pending, (state, action: any) => {
      state.jobwork = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobworkListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        state.jobwork = action.payload.data.data.records;
        state.totalJobwork = action.payload.data.data.total_records;
        state.totalAmount = action.payload.data.data.total_amount;
        state.error = null;
      } else {
        state.jobwork = [];
        state.totalAmount = 0;
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchJobworkListData.rejected, (state, action: any) => {
      state.jobwork = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
    builder.addCase(fetchItemsList.pending, (state, action: any) => {
      state.items = [];
    });
    builder.addCase(fetchItemsList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.items = action.payload.data.data.records;
      } else {
        state.items = [];
      }
    });
    builder.addCase(fetchItemsList.rejected, (state, action: any) => {
      state.items = [];
    });
    builder.addCase(fetchProcessesList.pending, (state, action: any) => {
      state.processes = [];
    });
    builder.addCase(fetchProcessesList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.processes = action.payload.data.data.records;
      } else {
        state.processes = [];
      }
    });
    builder.addCase(fetchProcessesList.rejected, (state, action: any) => {
      state.processes = [];
    });
    builder.addCase(fetchPartiesList.pending, (state, action: any) => {
      state.parties = [];
    });
    builder.addCase(fetchPartiesList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.parties = action.payload.data.data.records;
      } else {
        state.parties = [];
      }
    });
    builder.addCase(fetchPartiesList.rejected, (state, action: any) => {
      state.parties = [];
    });
    builder.addCase(fetchEditPartyItemsList.pending, (state, action: any) => {
      state.editPartyItems = [];
    });
    builder.addCase(fetchEditPartyItemsList.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.editPartyItems = action.payload.data.data;
      } else {
        state.editPartyItems = [];
      }
    });
    builder.addCase(fetchEditPartyItemsList.rejected, (state, action: any) => {
      state.editPartyItems = [];
    });
  },
});

export const { toggleJobworkModal, setEditJobwork, toggleJobworkPhotoModal } =
  jobworkSlice.actions;

export const getJobwork = (state: RootState) => state.jobwork.jobwork;

export const getTotalJobwork = (state: RootState) => state.jobwork.totalJobwork;

export const getTotalAmount = (state: RootState) => state.jobwork.totalAmount;

export const getIsJobworkModalOpen = (state: RootState) =>
  state.jobwork.isJobworkModalOpen;

export const getIsJobworkPhotoModalOpen = (state: RootState) =>
  state.jobwork.isJobworkPhotoModalOpen;

export const getEditJobwork = (state: RootState) => state.jobwork.editJobwork;

export const getEditPartyItems = (state: RootState) =>
  state.jobwork.editPartyItems;

export const getItems = (state: RootState) => state.jobwork.items;

export const getProcesses = (state: RootState) => state.jobwork.processes;

export const getParties = (state: RootState) => state.jobwork.parties;

export default jobworkSlice.reducer;
